import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtDateIT } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, X, AlertTriangle, FileX2, CheckCircle2 } from "lucide-react";

// Dialog "Conferma rifiuto": motivo strutturato, data effettiva, nota, riepilogo azioni.
// Annullare NON lascia modifiche parziali (nessuna scrittura senza Conferma).
export const RejectDialog = ({ practice, onClose, onDone }) => {
  const [reasons, setReasons] = useState(null);
  const [reasonId, setReasonId] = useState(practice.rejectReasonId || "");
  const [esitoDate, setEsitoDate] = useState((practice.esitoDate || "").slice(0, 10));
  const [nota, setNota] = useState(practice.rejectNote || "");
  const [preview, setPreview] = useState(null);
  const [recallChoice, setRecallChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    api.get("/reject-reasons").then((r) => setReasons(r.data.items || [])).catch(() => setReasons([]));
  }, []);

  const loadPreview = useCallback(async () => {
    if (!reasonId) { setPreview(null); return; }
    try {
      const r = await api.post("/reject-reasons/preview", { practice_id: practice.id, reason_id: reasonId, esito_date: esitoDate || null });
      setPreview(r.data); setErrMsg("");
    } catch (e) { setPreview(null); setErrMsg(e?.response?.data?.detail || "Errore anteprima"); }
  }, [practice.id, reasonId, esitoDate]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const conflict = preview?.warnings?.find((w) => w.code === "recall_diverso");
  const baseMissing = preview?.warnings?.some((w) => w.code === "base_mancante");
  const divieto = preview?.warnings?.some((w) => w.code === "divieto");
  const noLink = preview?.warnings?.some((w) => w.code === "senza_collegamento");

  const confirm = async () => {
    if (!reasonId) { toast.error("Seleziona il motivo del rifiuto"); return; }
    if (baseMissing) { toast.error("Completa la data effettiva del rifiuto: non viene mai inventata"); return; }
    if (conflict && !recallChoice) { toast.error("Recall già presente: scegli quale data mantenere"); return; }
    setBusy(true); setErrMsg("");
    try {
      const r = await api.post("/reject-reasons/apply", {
        practice_id: practice.id, reason_id: reasonId, esito_date: esitoDate || null,
        nota, recall_choice: recallChoice || null,
      });
      if (r.data.gia_eseguita) toast.info("Automazione già eseguita in precedenza: nessuna azione duplicata");
      else if (r.data.status === "pending_link") toast.warning("Rifiuto salvato. Automazione in attesa di collegamento anagrafica");
      else if (r.data.status === "partial") toast.warning("Rifiuto salvato, alcune azioni non eseguite: vedi dettaglio nella pratica");
      else toast.success("Rifiuto confermato e automazione eseguita");
      onDone(r.data);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "recall_diverso") setErrMsg(`Recall già presente (${fmtDateIT(det.attuale)}) diverso dalla proposta (${fmtDateIT(det.proposta)}): scegli quale mantenere qui sotto`);
      else setErrMsg(typeof det === "string" ? det : "Errore: nessuna azione applicata");
    } finally { setBusy(false); }
  };

  const rec = preview?.azioni?.recall;
  const az = preview?.azioni || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="reject-dialog">
      <div className="bg-white rounded-[3px] w-full max-w-lg shadow-xl flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <FileX2 className="w-4 h-4 text-rose-700" /> {practice.rejectReasonId ? "Modifica motivo di rifiuto" : "Conferma rifiuto"} — {practice.elemento || practice.cliente || "pratica"} ({practice.monthKey})
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="reject-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3 text-sm">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Motivo del rifiuto</label>
            <select value={reasonId} onChange={(e) => { setReasonId(e.target.value); setRecallChoice(""); }} data-testid="reject-reason-select"
              className="w-full mt-1 border border-slate-300 rounded-[3px] px-2 py-2 bg-white">
              <option value="">— seleziona —</option>
              {(reasons || []).filter((r) => r.active || r.id === practice.rejectReasonId).map((r) => (
                <option key={r.id} value={r.id}>{r.label}{!r.active ? " (disattivato)" : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Data effettiva del rifiuto</label>
            <input type="date" value={esitoDate} onChange={(e) => setEsitoDate(e.target.value)} data-testid="reject-esito-date"
              className="w-full mt-1 border border-slate-300 rounded-[3px] px-2 py-2" />
            <div className="text-[11px] text-slate-400 mt-0.5">Mai sostituita con la data di caricamento o modifica</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Nota (facoltativa)</label>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} data-testid="reject-note"
              className="w-full mt-1 border border-slate-300 rounded-[3px] px-2 py-1.5 text-sm" />
          </div>

          {preview && (
            <div className="border border-slate-200 rounded-[3px] p-3 space-y-1.5 bg-slate-50/60" data-testid="reject-preview">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Azioni previste dalla regola (v{preview.reason_version})</div>
              {rec && (
                <div className="text-xs">
                  Recall: <b className="tabular-nums">{fmtDateIT(rec.proposta)}</b>
                  {rec.dovuta && <span className="text-amber-700"> — Rivalutazione DOVUTA (data teorica {fmtDateIT(rec.teorica)} già trascorsa: proposto il primo giorno lavorativo)</span>}
                  {!rec.dovuta && rec.teorica !== rec.proposta && <span className="text-slate-500"> (teorica {fmtDateIT(rec.teorica)}, spostata al giorno lavorativo)</span>}
                  <span className="text-slate-500"> · base {rec.base} +{rec.mesi} mesi di calendario</span>
                </div>
              )}
              {!rec && <div className="text-xs text-slate-500">Nessun recall previsto da questa regola</div>}
              {az.portale && <div className="text-xs">Portafoglio: {az.portale.da || "da classificare"} → <b className="capitalize">{az.portale.a}</b> <span className="text-slate-500">({az.portale.nota})</span></div>}
              {az.crif && <div className="text-xs">Segnalato in CRIF: {az.crif.da} → <b>{az.crif.a}</b> <span className="text-slate-500">(con data e fonte, mai azzerato in automatico)</span></div>}
              {az.attivita && <div className="text-xs">Attività interna: "{az.attivita.titolo}"</div>}
              {az.nota && <div className="text-xs">Nota automatica sull'anagrafica: "{az.nota.testo}"</div>}
              {preview.client && <div className="text-[11px] text-slate-500">Cliente collegato: {preview.client.nome} · recall attuale {preview.client.recall_attuale ? fmtDateIT(preview.client.recall_attuale) : "nessuno"} · portafoglio {preview.client.portale_attuale || "da classificare"} · CRIF {preview.client.crif_attuale}</div>}
              {(preview.warnings || []).map((w, i) => (
                <div key={i} className={`text-xs flex items-start gap-1 ${w.code === "divieto" ? "text-rose-700" : "text-amber-700"}`}>
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {w.msg}
                </div>
              ))}
              {conflict && !divieto && (
                <div className="text-xs space-y-1 pt-1 border-t border-slate-200" data-testid="reject-recall-choice">
                  <label className="flex items-center gap-1.5"><input type="radio" name="rc" checked={recallChoice === "proposta"} onChange={() => setRecallChoice("proposta")} /> Usa la proposta: <b>{fmtDateIT(conflict.proposta)}</b></label>
                  <label className="flex items-center gap-1.5"><input type="radio" name="rc" checked={recallChoice === "mantieni"} onChange={() => setRecallChoice("mantieni")} /> Mantieni l'attuale: <b>{fmtDateIT(conflict.attuale)}</b></label>
                </div>
              )}
            </div>
          )}
          {errMsg && <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-[3px] p-2" data-testid="reject-error">{errMsg}</div>}
          <div className="text-[11px] text-slate-400">Gli intervalli sono regole organizzative di ricontatto: non tempi garantiti di cancellazione dalle banche dati, nessuna nuova richiesta di finanziamento automatica.</div>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex gap-2 shrink-0">
          <button onClick={onClose} data-testid="reject-cancel" className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-slate-50">Annulla (nessuna modifica)</button>
          <button onClick={confirm} disabled={busy || !reasonId || baseMissing || (conflict && !recallChoice && !noLink)} data-testid="reject-confirm"
            className="flex-1 px-3 py-2 text-sm bg-rose-700 text-white rounded-[3px] hover:bg-rose-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Conferma rifiuto
          </button>
        </div>
      </div>
    </div>
  );
};
